/**
 * Catàleg territorial de Catalunya — PlenariCAT
 * Font: Idescat — Codis territorials i d'entitats — Municipis
 * Data de la font: 02-08-2026
 *
 * Font única: src/data/catalog/municipalities.json i comarques.json
 * Generats amb: npm run import:territorial-catalog
 *
 * Compatibilitat legacy: muni-001 (Berga), muni-002 (Manresa), muni-003 (Vic)
 */

// ─── Models territorials ──────────────────────────────────────────────

export interface Comarca {
  id: string;
  codiOficial: string;
  nom: string;
  slug: string;
  tipus: 'comarca' | 'aran';
  provincia?: string | null;
}

export interface Municipality {
  id: string;
  codiOficial: string | null;
  nom: string;
  slug: string;
  comarcaId: string;
  provincia: string | null;
  disponible: boolean;
  dadesDemoDisponibles: boolean;
  /** @deprecated Utilitza disponible */
  actiu?: boolean;
}

export interface Workspace {
  id: string;
  municipalityId: string;
  nom: string;
  rolUsuari: string;
  actiu: boolean;
  creatA?: string;
}

export type TerritorialPresenceStatus =
  | 'candidatura_anunciada' | 'en_preparacio' | 'estructura_local' | 'activitat_informativa' | 'sense_dades';

export interface TerritorialPresence {
  municipalityId: string;
  status: TerritorialPresenceStatus;
  responsablePublic?: string;
  dataActualitzacio?: string;
  font?: string;
  notes?: string;
}

// ─── Import JSON fonts ────────────────────────────────────────────────

import municipalitiesJson from '@/data/catalog/municipalities.json';
import comarcesJson from '@/data/catalog/comarques.json';

// Cast JSON to typed arrays
const municipalitiesFromJson: Municipality[] = municipalitiesJson as Municipality[];
const comarcesFromJson: Comarca[] = comarcesJson as Comarca[];

// ─── Mapa de compatibilitat legacy ───────────────────────────────────
// Els seeds antics utilitzen muni-001, muni-002, muni-003.
// El catàleg oficial utilitza muni-XXXXXX (codi oficial 6 dígits).
//
// Per mantenir compatibilitat total amb mockData, demoStore i Workspaces,
// els municipis legacy conserven el seu id original (muni-001, etc.)
// i s'afegeix codiOficial per a la cerca per codi.

const LEGACY_CODI_TO_ID: Record<string, string> = {
  '080229': 'muni-001', // Berga
  '081136': 'muni-002', // Manresa
  '082981': 'muni-003', // Vic
};

// IDs canònics del JSON que han de ser substituïts pels legacy
const CANONICAL_TO_LEGACY: Record<string, string> = {
  'muni-080229': 'muni-001',
  'muni-081136': 'muni-002',
  'muni-082981': 'muni-003',
};

// Municipis amb dades demo disponibles
const DEMO_CODI_SET = new Set(['080229', '081136', '082981']);

// ─── Catàleg complet ──────────────────────────────────────────────────

export const comarques: Comarca[] = comarcesFromJson;

// Normalitza els municipis del JSON:
// - Substitueix els IDs canònics pels legacy per als 3 municipis principals
// - Marca dadesDemoDisponibles correctament
export const municipalities: Municipality[] = municipalitiesFromJson.map((m) => {
  const legacyId = CANONICAL_TO_LEGACY[m.id];
  const isDemo = DEMO_CODI_SET.has(m.codiOficial ?? '');
  if (legacyId) {
    return { ...m, id: legacyId, dadesDemoDisponibles: isDemo };
  }
  return { ...m, dadesDemoDisponibles: isDemo || m.dadesDemoDisponibles };
});

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Retorna el municipi pel seu id.
 * Suporta ids legacy (muni-001, muni-002, muni-003) i ids oficials (muni-XXXXXX).
 */
export function getMunicipalityById(id: string): Municipality | null {
  if (!id) return null;
  // Cerca directa (inclou legacy ids ja normalitzats)
  const direct = municipalities.find((m) => m.id === id);
  if (direct) return direct;
  // Cerca per codi oficial (per si l'id és el codi directament)
  return municipalities.find((m) => m.codiOficial === id) ?? null;
}

/** Retorna la comarca d'un municipi a partir del seu municipalityId */
export function getComarcaByMunicipalityId(municipalityId: string): Comarca | null {
  const muni = getMunicipalityById(municipalityId);
  if (!muni) return null;
  return comarques.find((c) => c.id === muni.comarcaId) ?? null;
}

/** Retorna el municipi pel seu codi oficial */
export function getMunicipalityByCodiOficial(codi: string): Municipality | null {
  // Comprova primer si hi ha un legacy id per a aquest codi
  const legacyId = LEGACY_CODI_TO_ID[codi];
  if (legacyId) {
    return municipalities.find((m) => m.id === legacyId) ?? null;
  }
  return municipalities.find((m) => m.codiOficial === codi) ?? null;
}

/** Retorna els municipis d'una comarca */
export function getMunicipalitiesByComarca(comarcaId: string): Municipality[] {
  return municipalities.filter((m) => m.comarcaId === comarcaId);
}

/** Retorna la comarca pel seu id */
export function getComarcaById(id: string): Comarca | null {
  return comarques.find((c) => c.id === id) ?? null;
}
